import { Button, Card, Col, Descriptions, Popconfirm, Row, Space, Spin, Tag, message } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { OwnerType } from '@never2busy/shared';
import { personsApi } from '../../../api/endpoints/persons';
import { activitiesApi } from '../../../api/endpoints/activities';
import { PageHeader } from '../../../components/common/PageHeader';
import { KeywordsManager } from '../../../components/common/KeywordsManager';

export function PersonDetailPage(): JSX.Element {
  const { id } = useParams();
  const personId = id ? parseInt(id, 10) : 0;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['person', personId],
    queryFn: () => personsApi.findOne(personId),
  });

  const activitiesQuery = useQuery({
    queryKey: ['activities', { personId }],
    queryFn: () => activitiesApi.list({ personId, page: 1, pageSize: 20 }),
    enabled: !!personId,
  });

  const removeMutation = useMutation({
    mutationFn: () => personsApi.remove(personId),
    onSuccess: () => {
      message.success('Contacto eliminado');
      void queryClient.invalidateQueries({ queryKey: ['persons'] });
      navigate('/contactos');
    },
    onError: () => message.error('No se pudo eliminar el contacto'),
  });

  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }
  if (!data) return <div>Contacto no encontrado</div>;

  const fullName = `${data.firstName ?? ''} ${data.lastName}`.trim();

  return (
    <>
      <PageHeader
        title={fullName}
        crumbs={[{ title: 'Contactos', to: '/contactos' }, { title: fullName }]}
        extra={
          <Space>
            <Button
              icon={<EditOutlined />}
              onClick={() => navigate(`/contactos/${data.id}/editar`)}
            >
              Editar
            </Button>
            <Popconfirm
              title="¿Eliminar este contacto?"
              okText="Sí, eliminar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
              onConfirm={() => removeMutation.mutate()}
            >
              <Button danger icon={<DeleteOutlined />} loading={removeMutation.isPending}>
                Eliminar
              </Button>
            </Popconfirm>
          </Space>
        }
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="Datos">
            <Descriptions column={{ xs: 1, sm: 2 }} size="small">
              <Descriptions.Item label="Cuenta">
                {data.account ? (
                  <Link to={`/cuentas/${data.account.id}`}>{data.account.name}</Link>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Puesto">{data.jobTitle ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Departamento">{data.department ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="¿Decisor?">
                {data.isDecisionMaker ? <Tag color="green">Sí</Tag> : 'No'}
              </Descriptions.Item>
              <Descriptions.Item label="Correo">{data.email ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Correo alterno">{data.emailAlt ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Teléfono">{data.phone ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Móvil">{data.mobile ?? '—'}</Descriptions.Item>
            </Descriptions>
            {data.notes && (
              <div style={{ marginTop: 16 }}>
                <strong>Notas:</strong>
                <div style={{ whiteSpace: 'pre-wrap' }}>{data.notes}</div>
              </div>
            )}
            <div style={{ marginTop: 16 }}>
              <strong>Etiquetas:</strong>
              <div style={{ marginTop: 8 }}>
                <KeywordsManager ownerType={OwnerType.PERSON} ownerId={data.id} />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Actividades recientes">
            {activitiesQuery.data?.data.length ? (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {activitiesQuery.data.data.map((a) => (
                  <li key={a.id} style={{ padding: '4px 0' }}>
                    <Link to={`/actividades/${a.id}`}>
                      {a.type ?? 'Actividad'} —{' '}
                      {a.notes?.slice(0, 60)}
                      {a.notes && a.notes.length > 60 ? '…' : ''}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <div style={{ color: '#888' }}>Sin actividades</div>
            )}
          </Card>
        </Col>
      </Row>
    </>
  );
}
